import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DatePicker } from 'rsuite';
import { Eye, ChevronDown, X, User, DollarSign } from 'lucide-react';
import TipTapEditor from './TipTapEditor';
import { useAuth } from '../context/AuthContext';
import {
    cotizacionesAPI,
    tiposServicioAPI,
    personalAPI,
    configuracionAPI,
    costosFijosAPI,
    plantillasAPI,
    clientesAPI,
    usuariosAPI
} from '../services/api';
import { moneyFmt, safeId, getCurrentDate } from '../utils/helpers';
import { generatePdfCotizacion } from '../utils/pdfGenerator';

// Estilos personalizados para react-select (tema oscuro)
const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.7)',
        borderColor: state.isFocused ? 'rgba(140, 220, 255, 0.5)' : 'rgba(140, 220, 255, 0.15)',
        borderRadius: '8px',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(140, 220, 255, 0.3)' : 'none',
        '&:hover': {
            borderColor: 'rgba(140, 220, 255, 0.3)'
        }
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.98)',
        border: '1px solid rgba(140, 220, 255, 0.2)',
        borderRadius: '8px',
        zIndex: 100
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
        // Scrollbar personalizado
        '::-webkit-scrollbar': {
            width: '6px'
        },
        '::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb': {
            background: 'rgba(140, 220, 255, 0.3)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(140, 220, 255, 0.5)'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'rgba(140, 220, 255, 0.2)'
            : state.isFocused
                ? 'rgba(140, 220, 255, 0.1)'
                : 'transparent',
        color: state.isSelected ? '#8cdcff' : '#e0e6ed',
        cursor: 'pointer',
        fontSize: '13px', // Fuente más pequeña para opciones (servicios)
        padding: '8px 12px',
        '&:active': {
            backgroundColor: 'rgba(140, 220, 255, 0.2)'
        }
    }),
    group: (base) => ({
        ...base,
        paddingTop: 8,
        paddingBottom: 0
    }),
    groupHeading: (base) => ({
        ...base,
        color: '#8cdcff',
        fontSize: '11px', // Categoría con tamaño definido
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 4,
        paddingLeft: 12
    }),
    singleValue: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    input: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    placeholder: (base) => ({
        ...base,
        color: 'rgba(224, 230, 237, 0.5)',
        fontSize: '14px'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: 'rgba(140, 220, 255, 0.5)',
        '&:hover': {
            color: 'rgba(140, 220, 255, 0.8)'
        }
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: 'rgba(140, 220, 255, 0.15)',
        borderRadius: '4px',
        border: '1px solid rgba(140, 220, 255, 0.25)'
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '13px',
        padding: '2px 6px'
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: 'rgba(224, 230, 237, 0.5)',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(248, 113, 113, 0.3)',
            color: '#f87171'
        }
    }),
    clearIndicator: (base) => ({
        ...base,
        color: 'rgba(140, 220, 255, 0.5)',
        padding: '6px',
        '&:hover': {
            color: 'rgba(248, 113, 113, 0.8)'
        }
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999
    })
};

const CotizacionForm = ({
    cotizacionId = null,
    tipoCambio,
    igvRate,
    rentabilidad = 0.68,
    rentabilidadCostoFijo,
    configuracion,
    onSaved,
    onCancel
}) => {
    // Obtener usuario logueado
    const { user, tienePermiso } = useAuth();
    const verDetalles = tienePermiso('ver_detalles_costos');
    const priceClass = verDetalles ? '' : 'price-hidden';
    const blurMoney = (value, moneda) => verDetalles ? moneyFmt(value, moneda) : '•••••';

    // Usar valor de prop o default si no se pasa (null/undefined)
    const rentCostoFijo = rentabilidadCostoFijo !== undefined && rentabilidadCostoFijo !== null
        ? rentabilidadCostoFijo
        : 0.68;
    // Estados
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tiposServicio, setTiposServicio] = useState([]); // Lista plana para compatibilidad
    const [tiposServicioGrouped, setTiposServicioGrouped] = useState([]); // Agrupados para react-select
    const [personalList, setPersonalList] = useState([]);
    const [costosFijosList, setCostosFijosList] = useState([]); // Lista plana de costos fijos
    const [costosFijosGrouped, setCostosFijosGrouped] = useState([]); // Agrupados por categoría
    const [numeroCotizacion, setNumeroCotizacion] = useState('');
    const [plantillasList, setPlantillasList] = useState([]); // Lista de plantillas activas
    const [clientesList, setClientesList] = useState([]); // Lista de clientes
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null); // Cliente seleccionado en el select
    const [showClienteModal, setShowClienteModal] = useState(false); // Modal para ver datos del cliente
    const [usuariosList, setUsuariosList] = useState([]); // Lista de usuarios para asignar

    // Datos de la cotizacion
    const [cotizacion, setCotizacion] = useState({
        cliente_id: null,
        cliente_nombre: '',
        cliente_email: '',
        cliente_telefono: '',
        cliente_empresa: '',
        tiempo_desarrollo: '',
        fecha: getCurrentDate(),
        validez: 15,
        moneda: 'PEN',
        comentarios: '',
        asignados_ids: []
    });

    // Items de servicio (cada uno con array de asignaciones)
    const [servicios, setServicios] = useState([]);

    // Acordeon: ID del servicio expandido (null = ninguno, pero el primero se abre por defecto)
    const [servicioExpandido, setServicioExpandido] = useState(null);

    // Items extras
    const [extras, setExtras] = useState([]);

    // Funcion para convertir costo segun moneda
    // Los costos en BD estan en PEN, si la moneda es USD dividimos por tipo de cambio
    const convertirCosto = useCallback((costoPEN) => {
        const costo = parseFloat(costoPEN) || 0;
        if (cotizacion.moneda === 'USD') {
            return costo / (parseFloat(tipoCambio) || 3.70);
        }
        return costo;
    }, [cotizacion.moneda, tipoCambio]);

    // Funcion para obtener costo por hora de un personal segun moneda actual
    const getCostoHoraPersonal = useCallback((personalId) => {
        const pers = personalList.find(p => p.id === parseInt(personalId));
        if (!pers) return 0;
        return convertirCosto(pers.costo_hora);
    }, [personalList, convertirCosto]);

    // Cargar datos iniciales
    useEffect(() => {
        const cargarDatos = async () => {
            setLoading(true);
            try {
                // Cargar tipos de servicio (planos y agrupados), personal, costos fijos, plantillas, clientes y usuarios
                const [tiposRes, tiposGroupedRes, personalRes, costosFijosRes, costosFijosGroupedRes, plantillasRes, clientesRes, usuariosRes] = await Promise.all([
                    tiposServicioAPI.getAll(),
                    tiposServicioAPI.getGrouped(),
                    personalAPI.getAll(),
                    costosFijosAPI.getAll(),
                    costosFijosAPI.getGrouped(),
                    plantillasAPI.getAll(),
                    clientesAPI.getAll(),
                    usuariosAPI.getAll()
                ]);

                setTiposServicio(tiposRes.data || []);
                setTiposServicioGrouped(tiposGroupedRes.data || []);
                setPersonalList(personalRes.data || []);
                setCostosFijosList(costosFijosRes.data || []);
                setCostosFijosGrouped(costosFijosGroupedRes.data || []);
                // Filtrar solo plantillas activas
                setPlantillasList((plantillasRes.data || []).filter(p => Boolean(p.activo)));
                setClientesList(clientesRes.data || []);
                // Filtrar solo usuarios activos
                setUsuariosList((usuariosRes.data || []).filter(u => u.activo));

                // Si es edicion, cargar cotizacion
                if (cotizacionId) {
                    const cotRes = await cotizacionesAPI.getById(cotizacionId);
                    const data = cotRes.data;

                    setCotizacion({
                        cliente_id: data.cliente_id || null,
                        cliente_nombre: data.cliente_nombre || '',
                        cliente_email: data.cliente_email || '',
                        cliente_telefono: data.cliente_telefono || '',
                        cliente_empresa: data.cliente_empresa || '',
                        tiempo_desarrollo: data.tiempo_desarrollo || '',
                        fecha: data.fecha?.split('T')[0] || getCurrentDate(),
                        validez: data.validez || 15,
                        moneda: data.moneda || 'PEN',
                        comentarios: data.comentarios || '',
                        asignados_ids: (data.asignados || []).map(a => a.usuario_id)
                    });

                    // Si tiene cliente_id, establecer el cliente seleccionado
                    if (data.cliente_id) {
                        const clienteEncontrado = (clientesRes.data || []).find(c => c.id === data.cliente_id);
                        if (clienteEncontrado) {
                            setClienteSeleccionado({
                                value: clienteEncontrado.id,
                                label: clienteEncontrado.empresa
                                    ? `${clienteEncontrado.nombre} - ${clienteEncontrado.empresa}`
                                    : clienteEncontrado.nombre,
                                data: clienteEncontrado
                            });
                        }
                    }

                    setNumeroCotizacion(data.numero);

                    // Mapear servicios con sus asignaciones, costos fijos y recalcular costo/total
                    const serviciosMapeados = (data.servicios || []).map(s => {
                        const asignacionesMapeadas = (s.asignaciones || []).map(a => ({
                            ...a,
                            _id: safeId(),
                            horas_adicionales: (parseFloat(a.horas) || 0) * 0.12,
                            subtotal: ((parseFloat(a.horas) || 0) + ((parseFloat(a.horas) || 0) * 0.12)) * (parseFloat(a.costo_hora) || 0)
                        }));

                        // Mapear costos fijos del servicio
                        const costosFijosMapeados = (s.costosFijos || []).map(cf => {
                            const costo = parseFloat(cf.costo) || 0;
                            const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;
                            return {
                                ...cf,
                                _id: safeId(),
                                costo: costo,
                                rentabilidad: rent,
                                subtotal: costo / (1 - rent)
                            };
                        });

                        // Calcular costo como suma de asignaciones
                        const costoServicio = asignacionesMapeadas.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);

                        // Calcular subtotal de costos fijos
                        const subtotalCostosFijos = costosFijosMapeados.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

                        return {
                            ...s,
                            _id: safeId(),
                            asignaciones: asignacionesMapeadas,
                            costosFijos: costosFijosMapeados,
                            costo: costoServicio,
                            total: (costoServicio / (1 - rentabilidad)) + subtotalCostosFijos
                        };
                    });
                    setServicios(serviciosMapeados);

                    setExtras(data.extras?.map(e => ({ ...e, _id: safeId() })) || []);
                } else {
                    // Obtener siguiente numero de cotizacion
                    const numRes = await configuracionAPI.getNextCotizacionNumber();
                    setNumeroCotizacion(numRes.data.numero);

                    // Asignar usuario logueado por defecto
                    if (user?.id) {
                        setCotizacion(c => ({ ...c, asignados_ids: [user.id] }));
                    }

                    // Agregar un servicio vacio por defecto
                    agregarServicioInicial();
                }
            } catch (err) {
                console.error('Error al cargar datos:', err);
                if (err.message.includes('No tienes acceso')) {
                    toast.error(err.message);
                    onCancel?.();
                } else {
                    toast.error('Error al cargar datos: ' + err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        cargarDatos();
    }, [cotizacionId, user]);

    // Funcion para agregar servicio vacio (usada en useEffect y boton)
    const agregarServicioInicial = () => {
        setServicios([{
            _id: safeId(),
            tipo_servicio_id: null,
            tipo_servicio_nombre: '',
            detalle: '',
            asignaciones: [],
            costosFijos: [],
            costo: 0,
            total: 0
        }]);
    };

    // Agregar servicio vacio
    const agregarServicio = () => {
        setServicios(prev => [...prev, {
            _id: safeId(),
            tipo_servicio_id: null,
            tipo_servicio_nombre: '',
            detalle: '',
            asignaciones: [],
            costosFijos: [],
            costo: 0,
            total: 0
        }]);
    };

    // Aplicar plantilla seleccionada
    const aplicarPlantilla = async (plantillaId) => {
        if (!plantillaId) return;

        try {
            const response = await plantillasAPI.getById(plantillaId);
            const plantilla = response.data;

            // Mapear servicios de la plantilla
            const serviciosMapeados = (plantilla.servicios || []).map(s => {
                // Mapear asignaciones de personal
                const asignacionesMapeadas = (s.asignaciones || []).map(a => {
                    const costoHora = getCostoHoraPersonal(a.personal_id);
                    const horas = parseFloat(a.horas) || 0;
                    const horasAdicionales = horas * 0.12;
                    const horasTotales = horas + horasAdicionales;

                    return {
                        _id: safeId(),
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: horas,
                        horas_adicionales: horasAdicionales,
                        costo_hora: costoHora,
                        rentabilidad: a.rentabilidad !== null ? parseFloat(a.rentabilidad) : rentabilidad,
                        subtotal: horasTotales * costoHora
                    };
                });

                // Mapear costos fijos
                const costosFijosMapeados = (s.costosFijos || []).map(cf => {
                    const costo = convertirCosto(cf.costo);
                    const rent = cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;

                    return {
                        _id: safeId(),
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        categoria_nombre: cf.categoria_nombre || '',
                        costo: costo,
                        rentabilidad: rent,
                        subtotal: costo / (1 - rent)
                    };
                });

                // Calcular totales
                const costoServicio = asignacionesMapeadas.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                const subtotalCostosFijos = costosFijosMapeados.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

                return {
                    _id: safeId(),
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    detalle: s.detalle || '',
                    asignaciones: asignacionesMapeadas,
                    costosFijos: costosFijosMapeados,
                    costo: costoServicio,
                    total: (costoServicio / (1 - rentabilidad)) + subtotalCostosFijos
                };
            });

            // Mapear extras de la plantilla
            const extrasMapeados = (plantilla.extras || []).map(e => ({
                _id: safeId(),
                detalle: e.detalle || '',
                costo: cotizacion.moneda === 'USD' ? (parseFloat(e.monto) || 0) / (parseFloat(tipoCambio) || 3.70) : (parseFloat(e.monto) || 0)
            }));

            setServicios(serviciosMapeados.length > 0 ? serviciosMapeados : []);
            setExtras(extrasMapeados);

            if (serviciosMapeados.length > 0) {
                setServicioExpandido(serviciosMapeados[0]._id);
            }

            toast.success(`Plantilla "${plantilla.nombre}" aplicada`);
        } catch (err) {
            console.error('Error al aplicar plantilla:', err);
            toast.error('Error al cargar la plantilla');
        }
    };

    // Actualizar datos del servicio (tipo, detalle)
    const actualizarServicio = (servicioId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const updated = { ...s, [campo]: valor };

            // Si cambia el tipo de servicio
            if (campo === 'tipo_servicio_id') {
                const tipo = tiposServicio.find(t => t.id === parseInt(valor));
                updated.tipo_servicio_nombre = tipo?.nombre || '';
                updated.detalle = tipo?.descripcion || s.detalle;
            }

            return updated;
        }));
    };

    // Agregar asignacion de personal a un servicio
    const agregarAsignacion = (servicioId) => {
        const primerPersonal = personalList[0];
        const costoHora = getCostoHoraPersonal(primerPersonal?.id);
        const horas = 10;
        const horasAdicionales = horas * 0.12;
        const horasTotales = horas + horasAdicionales;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevaAsignacion = {
                _id: safeId(),
                personal_id: primerPersonal?.id || null,
                personal_nombre: primerPersonal?.nombre || '',
                horas: horas,
                horas_adicionales: horasAdicionales,
                costo_hora: costoHora,
                subtotal: horasTotales * costoHora,
                rentabilidad: rentabilidad
            };

            const nuevasAsignaciones = [...s.asignaciones, nuevaAsignacion];
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Actualizar asignacion de personal
    const actualizarAsignacion = (servicioId, asignacionId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevasAsignaciones = s.asignaciones.map(a => {
                if (a._id !== asignacionId) return a;

                const updated = { ...a, [campo]: valor };

                // Si cambia el personal, actualizar nombre y costo convertido
                if (campo === 'personal_id') {
                    const pers = personalList.find(p => p.id === parseInt(valor));
                    updated.personal_nombre = pers?.nombre || '';
                    updated.costo_hora = getCostoHoraPersonal(valor);
                }

                // Recalcular subtotal con 12% adicional sobre horas
                if (['horas', 'costo_hora', 'personal_id'].includes(campo)) {
                    const horas = parseFloat(updated.horas) || 0;
                    const horasAdicionales = horas * 0.12;
                    const horasTotales = horas + horasAdicionales;
                    const costoHora = parseFloat(updated.costo_hora) || 0;
                    updated.horas_adicionales = horasAdicionales;
                    updated.subtotal = horasTotales * costoHora;
                }

                return updated;
            });

            // Recalcular costo y total del servicio
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar asignacion de personal
    const eliminarAsignacion = (servicioId, asignacionId) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevasAsignaciones = s.asignaciones.filter(a => a._id !== asignacionId);
            const costoAsignaciones = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const totalAsignaciones = nuevasAsignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);

            // Incluir costos fijos en el total
            const subtotalCostosFijos = (s.costosFijos || []).reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costo: costoAsignaciones,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // ============ COSTOS FIJOS DEL SERVICIO ============

    // Agregar costo fijo a un servicio (sin parámetro crea fila vacía con primer costo fijo)
    const agregarCostoFijoServicio = (servicioId, costoFijoId = null) => {
        // Si no se pasa ID, usar el primer costo fijo disponible
        const costoFijo = costoFijoId
            ? costosFijosList.find(cf => cf.id === parseInt(costoFijoId))
            : costosFijosList[0];

        if (!costoFijo) {
            toast.error('No hay costos fijos disponibles');
            return;
        }

        // Convertir costo si es USD
        const costoBase = parseFloat(costoFijo.costo) || 0;
        const costoConvertido = cotizacion.moneda === 'USD' ? costoBase / (parseFloat(tipoCambio) || 3.70) : costoBase;

        // Obtener rentabilidad del costo fijo desde la BD (o usar default)
        const rentabilidadCF = costoFijo.rentabilidad !== undefined && costoFijo.rentabilidad !== null ? parseFloat(costoFijo.rentabilidad) : rentCostoFijo;

        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevoCostoFijo = {
                _id: safeId(),
                costo_fijo_id: costoFijo.id,
                costo_fijo_nombre: costoFijo.nombre,
                categoria_nombre: costoFijo.categoria_nombre || '',
                costo: costoConvertido,
                rentabilidad: rentabilidadCF,
                subtotal: costoConvertido / (1 - rentabilidadCF)
            };

            const nuevosCostosFijos = [...(s.costosFijos || []), nuevoCostoFijo];
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            // Recalcular total del servicio incluyendo costos fijos (con rentabilidad individual por asignación)
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Actualizar costo fijo de un servicio
    const actualizarCostoFijoServicio = (servicioId, costoFijoItemId, campo, valor) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevosCostosFijos = (s.costosFijos || []).map(cf => {
                if (cf._id !== costoFijoItemId) return cf;

                const updated = { ...cf, [campo]: valor };

                // Si cambia el costo o rentabilidad, recalcular subtotal
                if (campo === 'costo' || campo === 'rentabilidad') {
                    const costo = parseFloat(campo === 'costo' ? valor : cf.costo) || 0;
                    const rentVal = campo === 'rentabilidad' ? valor : cf.rentabilidad;
                    const rent = rentVal !== undefined && rentVal !== null ? parseFloat(rentVal) : rentCostoFijo;
                    updated.subtotal = costo / (1 - rent);
                }

                return updated;
            });

            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            // Recalcular total del servicio incluyendo costos fijos
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar costo fijo de un servicio
    const eliminarCostoFijoServicio = (servicioId, costoFijoItemId) => {
        setServicios(prev => prev.map(s => {
            if (s._id !== servicioId) return s;

            const nuevosCostosFijos = (s.costosFijos || []).filter(cf => cf._id !== costoFijoItemId);
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            // Recalcular total del servicio incluyendo costos fijos (con rentabilidad individual por asignación)
            const totalAsignaciones = s.asignaciones.reduce((acc, a) => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                return acc + ((parseFloat(a.subtotal) || 0) / (1 - rent));
            }, 0);

            return {
                ...s,
                costosFijos: nuevosCostosFijos,
                total: totalAsignaciones + subtotalCostosFijos
            };
        }));
    };

    // Eliminar servicio completo
    const eliminarServicio = (servicioId) => {
        setServicios(prev => prev.filter(s => s._id !== servicioId));
    };

    // Cambiar moneda y recalcular todos los costos
    const handleCambiarMoneda = (nuevaMoneda) => {
        if (nuevaMoneda === cotizacion.moneda) return;

        const tc = parseFloat(tipoCambio) || 3.70;
        const monedaActual = cotizacion.moneda;

        // Recalcular costos de todos los servicios
        setServicios(prev => prev.map(s => {
            // Recalcular asignaciones
            const nuevasAsignaciones = s.asignaciones.map(a => {
                // Obtener costo original en PEN del personal
                const pers = personalList.find(p => p.id === parseInt(a.personal_id));
                const costoBasePEN = parseFloat(pers?.costo_hora) || 0;

                // Convertir segun la nueva moneda
                const nuevoCosto = nuevaMoneda === 'USD' ? costoBasePEN / tc : costoBasePEN;
                const horas = parseFloat(a.horas) || 0;
                const horasAdicionales = horas * 0.12;
                const horasTotales = horas + horasAdicionales;
                const nuevoSubtotal = horasTotales * nuevoCosto;

                return {
                    ...a,
                    costo_hora: nuevoCosto,
                    horas_adicionales: horasAdicionales,
                    subtotal: nuevoSubtotal
                };
            });

            // Recalcular costos fijos
            const nuevosCostosFijos = (s.costosFijos || []).map(cf => {
                // Obtener costo original en PEN del costo fijo
                const cfOriginal = costosFijosList.find(c => c.id === cf.costo_fijo_id);
                const costoBasePEN = parseFloat(cfOriginal?.costo) || parseFloat(cf.costo) || 0;

                // Convertir segun la nueva moneda
                const nuevoCosto = nuevaMoneda === 'USD' ? costoBasePEN / tc : costoBasePEN;
                const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo;

                return {
                    ...cf,
                    costo: nuevoCosto,
                    subtotal: nuevoCosto / (1 - rent)
                };
            });

            // Calcular costo como suma de asignaciones
            const costoServicio = nuevasAsignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
            const subtotalCostosFijos = nuevosCostosFijos.reduce((acc, cf) => acc + (parseFloat(cf.subtotal) || 0), 0);

            return {
                ...s,
                asignaciones: nuevasAsignaciones,
                costosFijos: nuevosCostosFijos,
                costo: costoServicio,
                total: (costoServicio / (1 - rentabilidad)) + subtotalCostosFijos
            };
        }));

        // Recalcular costos de extras
        setExtras(prev => prev.map(e => {
            const costoActual = parseFloat(e.costo) || 0;
            let nuevoCosto;

            if (monedaActual === 'PEN' && nuevaMoneda === 'USD') {
                // De PEN a USD: dividir por TC
                nuevoCosto = costoActual / tc;
            } else if (monedaActual === 'USD' && nuevaMoneda === 'PEN') {
                // De USD a PEN: multiplicar por TC
                nuevoCosto = costoActual * tc;
            } else {
                nuevoCosto = costoActual;
            }

            return {
                ...e,
                costo: nuevoCosto
            };
        }));

        setCotizacion(c => ({ ...c, moneda: nuevaMoneda }));
    };

    // Agregar extra
    const agregarExtra = () => {
        setExtras(prev => [...prev, {
            _id: safeId(),
            detalle: '',
            costo: 0
        }]);
    };

    // Actualizar extra
    const actualizarExtra = (id, campo, valor) => {
        setExtras(prev => prev.map(e => {
            if (e._id !== id) return e;
            // Asegurar que costo sea numero
            const valorFinal = campo === 'costo' ? (parseFloat(valor) || 0) : valor;
            return { ...e, [campo]: valorFinal };
        }));
    };

    // Eliminar extra
    const eliminarExtra = (id) => {
        setExtras(prev => prev.filter(e => e._id !== id));
    };

    // Calculos
    const computed = useMemo(() => {
        // Calcular costo total y ganancia total sumando valores individuales
        let costoTotal = 0;
        let gananciaTotal = 0;

        servicios.forEach(s => {
            // Costos y ganancias de asignaciones de personal
            (s.asignaciones || []).forEach(a => {
                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null ? a.rentabilidad : rentabilidad;
                const subtotalAsig = parseFloat(a.subtotal) || 0;
                const totalAsig = subtotalAsig / (1 - rent);
                costoTotal += subtotalAsig;
                gananciaTotal += totalAsig - subtotalAsig;
            });

            // Costos y ganancias de costos fijos
            (s.costosFijos || []).forEach(cf => {
                const costoCF = parseFloat(cf.costo) || 0;
                const subtotalCF = parseFloat(cf.subtotal) || 0;
                costoTotal += costoCF;
                gananciaTotal += subtotalCF - costoCF;
            });
        });

        // Subtotal: suma de todos los "Total" de headers de servicio (va al PDF)
        const subtotalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);

        // Subtotal Extras (va al PDF)
        const subtotalExtras = extras.reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);

        // Subtotal general (servicios sin extras, para calcular IGV)
        const subtotal = subtotalServicios;

        // IGV sobre subtotal de servicios (va al PDF)
        const igv = subtotal * (igvRate || 0.18);

        // Total final: subtotal + IGV + extras (va al PDF)
        const total = subtotal + igv + subtotalExtras;

        // Conversion para mostrar en la otra moneda
        const rate = parseFloat(tipoCambio) || 3.70;
        const otherCurrency = cotizacion.moneda === "PEN" ? "USD" : "PEN";
        const totalOther = cotizacion.moneda === "PEN" ? total / rate : total * rate;

        return {
            costoTotal,        // interno
            subtotalServicios, // va al PDF
            subtotalExtras,    // va al PDF
            subtotal,          // va al PDF
            igv,               // va al PDF
            gananciaTotal,     // interno
            total,             // va al PDF
            otherCurrency,
            totalOther
        };
    }, [servicios, extras, igvRate, tipoCambio, cotizacion.moneda, rentabilidad]);

    // Guardar cotizacion
    const handleGuardar = async (e) => {
        e.preventDefault();

        if (!cotizacion.cliente_nombre.trim()) {
            toast.error('El nombre del cliente es obligatorio');
            return;
        }

        if (servicios.length === 0) {
            toast.error('Debe agregar al menos un servicio');
            return;
        }

        setSaving(true);

        try {
            // Auto crear/actualizar cliente
            const clienteData = {
                nombre: cotizacion.cliente_nombre.trim(),
                empresa: cotizacion.cliente_empresa.trim(),
                email: cotizacion.cliente_email.trim(),
                telefono: cotizacion.cliente_telefono.trim()
            };

            const clienteExistente = clientesList.find(c =>
                (c.nombre || '').toLowerCase() === clienteData.nombre.toLowerCase() &&
                (c.empresa || '').toLowerCase() === clienteData.empresa.toLowerCase() &&
                (c.email || '').toLowerCase() === clienteData.email.toLowerCase()
            );

            try {
                if (clienteExistente) {
                    await clientesAPI.update(clienteExistente.id, clienteData);
                    cotizacion.cliente_id = clienteExistente.id;
                } else {
                    const clienteRes = await clientesAPI.create(clienteData);
                    cotizacion.cliente_id = clienteRes.data.id;
                }
            } catch (clienteErr) {
                console.error('Error al guardar cliente:', clienteErr);
            }

            const data = {
                ...cotizacion,
                tipo_cambio: tipoCambio,
                servicios: servicios.map(s => ({
                    tipo_servicio_id: s.tipo_servicio_id,
                    tipo_servicio_nombre: s.tipo_servicio_nombre,
                    detalle: s.detalle,
                    costo: parseFloat(s.costo) || 0,
                    total: parseFloat(s.total) || 0,
                    asignaciones: s.asignaciones.map(a => ({
                        personal_id: a.personal_id,
                        personal_nombre: a.personal_nombre,
                        horas: parseFloat(a.horas) || 0,
                        costo_hora: parseFloat(a.costo_hora) || 0,
                        subtotal: parseFloat(a.subtotal) || 0
                    })),
                    costosFijos: (s.costosFijos || []).map(cf => ({
                        costo_fijo_id: cf.costo_fijo_id,
                        costo_fijo_nombre: cf.costo_fijo_nombre,
                        categoria_nombre: cf.categoria_nombre || '',
                        costo: parseFloat(cf.costo) || 0,
                        rentabilidad: cf.rentabilidad !== undefined && cf.rentabilidad !== null ? parseFloat(cf.rentabilidad) : rentCostoFijo,
                        subtotal: parseFloat(cf.subtotal) || 0
                    }))
                })),
                extras: extras.map(e => ({
                    detalle: e.detalle,
                    costo: parseFloat(e.costo) || 0
                }))
            };

            let response;
            if (cotizacionId) {
                response = await cotizacionesAPI.update(cotizacionId, data);
                toast.success('Cotización actualizada');
                // Al actualizar, no salir del formulario
            } else {
                response = await cotizacionesAPI.create(data);
                toast.success(`Cotización creada: ${response.data.numero}`);
                // Al crear nueva, volver al listado
                if (onSaved) onSaved(response.data);
            }

        } catch (err) {
            toast.error('Error al guardar: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    // Generar PDF (preview = abrir en nueva pestaña, false = descargar)
    const handleGenerarPDF = async (preview = false) => {
        try {
            await generatePdfCotizacion({
                cotizacion: {
                    ...cotizacion,
                    numero: numeroCotizacion,
                    subtotal: computed.subtotal,
                    subtotalExtras: computed.subtotalExtras,
                    igv: computed.igv,
                    total: computed.total
                },
                servicios,
                extras,
                configuracion,
                tiposServicio,
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando...</div>;
    }

    return (
        <form onSubmit={handleGuardar}>
            {/* Selector de plantilla (solo para cotización nueva) */}
            {!cotizacionId && plantillasList.length > 0 && (
                <section className="card">
                    <div className="cardHead">
                        <h2 className="h2">Usar Plantilla</h2>
                    </div>
                    <div style={{ maxWidth: '400px' }}>
                        <Select
                            styles={selectStyles}
                            options={plantillasList.map(p => ({
                                value: p.id,
                                label: p.nombre
                            }))}
                            onChange={(selected) => selected && aplicarPlantilla(selected.value)}
                            placeholder="Seleccionar plantilla..."
                            isClearable
                            isSearchable
                            noOptionsMessage={() => 'Sin plantillas'}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                        <span style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                            Carga automáticamente los servicios y extras predefinidos
                        </span>
                    </div>
                </section>
            )}

            {/* Datos del cliente + Moneda en 2 columnas */}
            <div className="form-top-row">
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">
                        {cotizacionId ? `Editar Cotización ${numeroCotizacion}` : `Nueva Cotización ${numeroCotizacion}`}
                    </h2>
                </div>

                {/* Selector de cliente y usuario asignado en 2 columnas */}
                <div className="form-selects-row">
                    {/* Selector de cliente existente */}
                    <div className="field">
                        <span className="label">Buscar cliente existente</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <Select
                                    options={clientesList.map(c => ({
                                        value: c.id,
                                        label: c.empresa ? `${c.nombre} - ${c.empresa}` : c.nombre,
                                        data: c
                                    }))}
                                    value={clienteSeleccionado}
                                    onChange={(selected) => {
                                        setClienteSeleccionado(selected);
                                        if (selected) {
                                            setCotizacion(c => ({
                                                ...c,
                                                cliente_id: selected.data.id,
                                                cliente_nombre: selected.data.nombre || '',
                                                cliente_empresa: selected.data.empresa || '',
                                                cliente_email: selected.data.email || '',
                                                cliente_telefono: selected.data.telefono || ''
                                            }));
                                        }
                                    }}
                                    isClearable
                                    placeholder="Buscar por nombre o empresa..."
                                    noOptionsMessage={() => 'No hay clientes'}
                                    styles={selectStyles}
                                />
                            </div>
                            {clienteSeleccionado && (
                                <button
                                    type="button"
                                    className="btn ghost"
                                    onClick={() => setShowClienteModal(true)}
                                    title="Ver datos del cliente"
                                    style={{ padding: '8px 12px' }}
                                >
                                    <Eye size={18} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Selector de usuarios asignados */}
                    <div className="field">
                        <span className="label">Asignar cotización a usuarios</span>
                        <Select
                            isMulti
                            options={[
                                { label: 'Super Admin', options: usuariosList.filter(u => u.rol === 'superadmin').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Admin', options: usuariosList.filter(u => u.rol === 'admin').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Gestor de Proyectos', options: usuariosList.filter(u => u.rol === 'gestor_proyecto').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Comercial', options: usuariosList.filter(u => u.rol === 'comercial').map(u => ({ value: u.id, label: u.nombre })) },
                                { label: 'Personal', options: usuariosList.filter(u => u.rol === 'personal').map(u => ({ value: u.id, label: u.nombre })) },
                            ].filter(g => g.options.length > 0)}
                            value={cotizacion.asignados_ids.map(id => {
                                const u = usuariosList.find(u => u.id === id);
                                return u ? { value: u.id, label: u.nombre } : null;
                            }).filter(Boolean)}
                            onChange={(selected) => {
                                setCotizacion(c => ({
                                    ...c,
                                    asignados_ids: (selected || []).map(s => s.value)
                                }));
                            }}
                            placeholder="Seleccionar usuarios..."
                            noOptionsMessage={() => 'No hay usuarios'}
                            styles={selectStyles}
                        />
                    </div>
                </div>

                <div className="form-grid-3">
                    <label className="field">
                        <span className="label">Cliente *</span>
                        <input
                            className="input"
                            type="text"
                            value={cotizacion.cliente_nombre}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_nombre: e.target.value }))}
                            required
                        />
                    </label>

                    <label className="field">
                        <span className="label">Empresa</span>
                        <input
                            className="input"
                            type="text"
                            value={cotizacion.cliente_empresa}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_empresa: e.target.value }))}
                        />
                    </label>

                    <label className="field">
                        <span className="label">Email</span>
                        <input
                            className="input"
                            type="email"
                            value={cotizacion.cliente_email}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_email: e.target.value }))}
                        />
                    </label>

                    <label className="field">
                        <span className="label">Teléfono</span>
                        <input
                            className="input"
                            type="text"
                            value={cotizacion.cliente_telefono}
                            onChange={(e) => setCotizacion(c => ({ ...c, cliente_telefono: e.target.value }))}
                        />
                    </label>

                    <label className="field">
                        <span className="label">Tiempo de desarrollo</span>
                        <input
                            className="input"
                            type="text"
                            value={cotizacion.tiempo_desarrollo}
                            onChange={(e) => setCotizacion(c => ({ ...c, tiempo_desarrollo: e.target.value }))}
                            placeholder="Ej: 2 semanas, 1 mes"
                        />
                    </label>

                    <label className="field">
                        <span className="label">Fecha</span>
                        <DatePicker
                            value={cotizacion.fecha ? new Date(cotizacion.fecha + 'T00:00:00') : null}
                            onChange={(date) => setCotizacion(c => ({
                                ...c,
                                fecha: date ? date.toISOString().split('T')[0] : ''
                            }))}
                            format="dd/MM/yyyy"
                            oneTap
                            placement="bottomStart"
                            className="rs-theme-dark"
                            block
                        />
                    </label>

                    <label className="field">
                        <span className="label">Validez (dias)</span>
                        <input
                            className="input"
                            type="number"
                            min="1"
                            value={cotizacion.validez}
                            onChange={(e) => setCotizacion(c => ({ ...c, validez: parseInt(e.target.value) || 15 }))}
                        />
                    </label>
                </div>
            </section>

            {/* Seleccion de Moneda - ANTES de servicios */}
            <section className="card moneda-card">
                <div className="cardHead">
                    <h2 className="h2">Moneda de la Cotización</h2>
                </div>

                <div className="moneda-buttons">
                    <button
                        type="button"
                        className={`btn moneda-btn ${cotizacion.moneda === 'PEN' ? 'neon' : 'ghost'}`}
                        onClick={() => handleCambiarMoneda('PEN')}
                    >
                        Soles (PEN)
                    </button>
                    <button
                        type="button"
                        className={`btn moneda-btn ${cotizacion.moneda === 'USD' ? 'neon' : 'ghost'}`}
                        onClick={() => handleCambiarMoneda('USD')}
                    >
                        Dolares (USD)
                    </button>
                </div>

                <div className="moneda-info">
                    <span className="tc-badge">TC: 1 USD = {tipoCambio} PEN</span>
                    <span className="igv-badge">IGV: {Math.round(igvRate * 100)}%</span>
                </div>
            </section>
            </div>

            {/* Servicios */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Servicios</h2>
                    <button
                        className="btn neon"
                        type="button"
                        onClick={agregarServicio}
                    >
                        + Agregar servicio
                    </button>
                </div>

                <div className="servicios-list">
                    {servicios.map((servicio, idx) => {
                        // Solo abierto si está seleccionado explícitamente
                        const isExpanded = servicioExpandido === servicio._id;

                        // Al hacer clic: si ya está abierto lo cierra, si no lo abre (cerrando otros)
                        const toggleExpand = () => setServicioExpandido(isExpanded ? null : servicio._id);

                        return (
                            <div key={servicio._id} className={`servicio-item ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                <div
                                    className="servicio-header"
                                    onClick={toggleExpand}
                                >
                                    <div className="servicio-header-left">
                                        <span className="servicio-number">#{idx + 1}</span>
                                        <div className="select-wrapper" onClick={(e) => {
                                            e.stopPropagation();
                                            if (!isExpanded) {
                                                setServicioExpandido(servicio._id);
                                            }
                                        }}>
                                            <Select
                                                styles={selectStyles}
                                                options={tiposServicioGrouped}
                                                value={
                                                    tiposServicioGrouped
                                                        .flatMap(g => g.options)
                                                        .find(opt => opt.value === servicio.tipo_servicio_id) || null
                                                }
                                                onChange={(selected) => actualizarServicio(servicio._id, 'tipo_servicio_id', selected?.value || '')}
                                                placeholder="Seleccionar servicio..."
                                                isClearable
                                                isSearchable
                                                isDisabled={!isExpanded}
                                                noOptionsMessage={() => 'Sin opciones'}
                                            />
                                        </div>
                                    </div>
                                    <div className="servicio-header-right">
                                        <span className={`servicio-total ${priceClass}`}>
                                            Total: {blurMoney(servicio.total, cotizacion.moneda)}
                                        </span>
                                        <span className={`toggle-icon ${isExpanded ? 'rotated' : ''}`}>
                                            <ChevronDown size={20} />
                                        </span>
                                        <button
                                            className="btn-icon delete"
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                eliminarServicio(servicio._id);
                                            }}
                                            title="Eliminar servicio"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className={`servicio-content ${isExpanded ? 'open' : ''}`}>
                                    <div className="servicio-content-inner">
                                        <div className="servicio-detalle">
                                            <div className="field">
                                                <span className="label">Detalle del servicio</span>
                                                <TipTapEditor
                                                    value={servicio.detalle}
                                                    onChange={(html) => actualizarServicio(servicio._id, 'detalle', html)}
                                                    placeholder="Descripcion detallada del servicio..."
                                                />
                                            </div>
                                        </div>

                                        {/* Sección de Costos de Servicio */}
                                        <div className="costos-servicio-section">
                                            <div className="costos-servicio-header">
                                                <span className="costos-servicio-title">COSTOS DE SERVICIO</span>
                                                <div className="costos-servicio-buttons">
                                                    <button
                                                        className="btn-add btn-add-personal"
                                                        type="button"
                                                        onClick={() => agregarAsignacion(servicio._id)}
                                                        title="Agregar personal al servicio"
                                                    >
                                                        <span className="btn-add-plus">+</span>
                                                        <User size={16} />
                                                        Personal
                                                    </button>
                                                    <button
                                                        className="btn-add btn-add-costo-fijo"
                                                        type="button"
                                                        onClick={() => agregarCostoFijoServicio(servicio._id)}
                                                        title="Agregar costo fijo al servicio"
                                                    >
                                                        <span className="btn-add-plus">+</span>
                                                        <DollarSign size={16} />
                                                        Costo Fijo
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Divisor PERSONAL */}
                                            <div className="costos-divider personal-divider">
                                                <User size={16} />
                                                <span>PERSONAL</span>
                                            </div>

                                            {/* Tabla de Personal */}
                                            {(servicio.asignaciones || []).length > 0 && (
                                            <div className="asignaciones-table">
                                                <div className="asignaciones-row asignaciones-header-row">
                                                    <div className="asig-col asig-personal">Personal</div>
                                                    <div className="asig-col asig-horas">Horas</div>
                                                    {verDetalles && <div className="asig-col asig-adicional">12% Adic.</div>}
                                                    {verDetalles && <div className="asig-col asig-costo">Costo/hr</div>}
                                                    {verDetalles && <div className="asig-col asig-rentabilidad">Rentab.</div>}
                                                    {verDetalles && <div className="asig-col asig-subtotal">Subtotal</div>}
                                                    {verDetalles && <div className="asig-col asig-plus"></div>}
                                                    {verDetalles && <div className="asig-col asig-ganancia">Ganancia</div>}
                                                    <div className="asig-col asig-total">Total</div>
                                                    <div className="asig-col asig-actions"></div>
                                                </div>

                                                {(servicio.asignaciones || []).map((asig) => (
                                                    <div key={asig._id} className="asignaciones-row">
                                                        <div className="asig-col asig-personal">
                                                            <Select
                                                                styles={selectStyles}
                                                                options={personalList.map(p => ({
                                                                    value: p.id,
                                                                    label: verDetalles ? `${p.nombre} (${moneyFmt(convertirCosto(p.costo_hora), cotizacion.moneda)}/hr)` : p.nombre
                                                                }))}
                                                                value={
                                                                    asig.personal_id
                                                                        ? {
                                                                            value: asig.personal_id,
                                                                            label: (() => {
                                                                                const p = personalList.find(p => p.id === asig.personal_id);
                                                                                if (!p) return '';
                                                                                return verDetalles ? `${p.nombre} (${moneyFmt(convertirCosto(p.costo_hora), cotizacion.moneda)}/hr)` : p.nombre;
                                                                            })()
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(selected) => actualizarAsignacion(servicio._id, asig._id, 'personal_id', selected?.value || '')}
                                                                placeholder="Seleccionar..."
                                                                isClearable
                                                                isSearchable
                                                                noOptionsMessage={() => 'Sin opciones'}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                        <div className="asig-col asig-horas">
                                                            <input
                                                                className="input input-sm"
                                                                type="number"
                                                                min="0"
                                                                step="0.5"
                                                                value={asig.horas}
                                                                onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'horas', e.target.value)}
                                                            />
                                                        </div>
                                                        {verDetalles && <div className="asig-col asig-adicional">
                                                            <span className="adicional-value">+{(asig.horas_adicionales || asig.horas * 0.12).toFixed(2)}</span>
                                                        </div>}
                                                        {verDetalles && <div className="asig-col asig-costo">
                                                            <input
                                                                className="input input-sm"
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={asig.costo_hora}
                                                                disabled
                                                            />
                                                        </div>}
                                                        {verDetalles && <div className="asig-col asig-rentabilidad">
                                                            <div className="rentabilidad-input-wrapper">
                                                                <input
                                                                    className="input input-sm input-rentabilidad"
                                                                    type="number"
                                                                    min="0"
                                                                    max="99"
                                                                    step="1"
                                                                    value={Math.round((asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad) * 100)}
                                                                    onChange={(e) => actualizarAsignacion(servicio._id, asig._id, 'rentabilidad', (parseFloat(e.target.value) || 0) / 100)}
                                                                />
                                                                <span className="rentabilidad-percent">%</span>
                                                            </div>
                                                        </div>}
                                                        {verDetalles && <div className="asig-col asig-subtotal">
                                                            <span className="subtotal-value">
                                                                {moneyFmt(asig.subtotal, cotizacion.moneda)}
                                                            </span>
                                                        </div>}
                                                        {verDetalles && <div className="asig-col asig-plus">
                                                            <span className="plus-symbol">+</span>
                                                        </div>}
                                                        {verDetalles && <div className="asig-col asig-ganancia">
                                                            <span className="ganancia-value">
                                                                {(() => {
                                                                    const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                    return moneyFmt((asig.subtotal / (1 - rent)) - asig.subtotal, cotizacion.moneda);
                                                                })()}
                                                            </span>
                                                        </div>}
                                                        <div className={`asig-col asig-total ${priceClass}`}>
                                                            <span className="total-value">
                                                                {(() => {
                                                                    const rent = asig.rentabilidad !== undefined && asig.rentabilidad !== null ? asig.rentabilidad : rentabilidad;
                                                                    return blurMoney(asig.subtotal / (1 - rent), cotizacion.moneda);
                                                                })()}
                                                            </span>
                                                        </div>
                                                        <div className="asig-col asig-actions">
                                                            <button
                                                                className="btn ghost small"
                                                                type="button"
                                                                onClick={() => eliminarAsignacion(servicio._id, asig._id)}
                                                                title="Eliminar"
                                                            >
                                                                x
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            )}

                                            {/* Divisor COSTOS FIJOS */}
                                            <div className="costos-divider costos-fijos-divider">
                                                <DollarSign size={16} />
                                                <span>COSTOS FIJOS</span>
                                            </div>

                                            {(servicio.costosFijos || []).length > 0 && (
                                                <div className="costos-fijos-table">
                                                        <div className="costos-fijos-row costos-fijos-header-row">
                                                            <div className="cf-col cf-select">Costo Fijo</div>
                                                            {verDetalles && <div className="cf-col cf-costo">Costo</div>}
                                                            {verDetalles && <div className="cf-col cf-rentabilidad">Rentab.</div>}
                                                            {verDetalles && <div className="cf-col cf-subtotal">Subtotal</div>}
                                                            {verDetalles && <div className="cf-col cf-plus"></div>}
                                                            {verDetalles && <div className="cf-col cf-ganancia">Ganancia</div>}
                                                            <div className="cf-col cf-total">Total</div>
                                                            <div className="cf-col cf-actions"></div>
                                                        </div>

                                                        {(servicio.costosFijos || []).map((cf) => (
                                                            <div key={cf._id} className="costos-fijos-row">
                                                                <div className="cf-col cf-select">
                                                                    <Select
                                                                        styles={selectStyles}
                                                                        options={costosFijosGrouped}
                                                                        value={
                                                                            cf.costo_fijo_id
                                                                                ? costosFijosGrouped
                                                                                    .flatMap(g => g.options)
                                                                                    .find(opt => opt.value === cf.costo_fijo_id) || { value: cf.costo_fijo_id, label: cf.costo_fijo_nombre }
                                                                                : null
                                                                        }
                                                                        onChange={(selected) => {
                                                                            if (selected?.value) {
                                                                                const cfData = costosFijosList.find(c => c.id === selected.value);
                                                                                if (cfData) {
                                                                                    const costoConvertido = cotizacion.moneda === 'USD'
                                                                                        ? (parseFloat(cfData.costo) || 0) / (parseFloat(tipoCambio) || 3.70)
                                                                                        : parseFloat(cfData.costo) || 0;
                                                                                    const rentCF = cfData.rentabilidad !== undefined && cfData.rentabilidad !== null ? parseFloat(cfData.rentabilidad) : rentCostoFijo;
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'costo_fijo_id', selected.value);
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'costo_fijo_nombre', cfData.nombre);
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'categoria_nombre', cfData.categoria_nombre || '');
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'rentabilidad', rentCF);
                                                                                    actualizarCostoFijoServicio(servicio._id, cf._id, 'costo', costoConvertido);
                                                                                }
                                                                            }
                                                                        }}
                                                                        placeholder="Seleccionar costo fijo..."
                                                                        isSearchable
                                                                        noOptionsMessage={() => 'Sin opciones'}
                                                                        menuPortalTarget={document.body}
                                                                        menuPosition="fixed"
                                                                    />
                                                                </div>
                                                                {verDetalles && <div className="cf-col cf-costo">
                                                                    <input
                                                                        className="input input-sm"
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={cf.costo}
                                                                        onChange={(e) => actualizarCostoFijoServicio(servicio._id, cf._id, 'costo', e.target.value)}
                                                                    />
                                                                </div>}
                                                                {verDetalles && <div className="cf-col cf-rentabilidad">
                                                                    <div className="rentabilidad-input-wrapper">
                                                                        <input
                                                                            className="input input-sm input-rentabilidad"
                                                                            type="number"
                                                                            min="0"
                                                                            max="99"
                                                                            step="1"
                                                                            value={Math.round((cf.rentabilidad !== undefined && cf.rentabilidad !== null ? cf.rentabilidad : rentCostoFijo) * 100)}
                                                                            onChange={(e) => actualizarCostoFijoServicio(servicio._id, cf._id, 'rentabilidad', (parseFloat(e.target.value) || 0) / 100)}
                                                                        />
                                                                        <span className="rentabilidad-percent">%</span>
                                                                    </div>
                                                                </div>}
                                                                {verDetalles && <div className="cf-col cf-subtotal">
                                                                    <span className="subtotal-value">
                                                                        {moneyFmt(parseFloat(cf.costo) || 0, cotizacion.moneda)}
                                                                    </span>
                                                                </div>}
                                                                {verDetalles && <div className="cf-col cf-plus">
                                                                    <span className="plus-symbol">+</span>
                                                                </div>}
                                                                {verDetalles && <div className="cf-col cf-ganancia">
                                                                    <span className="ganancia-value">
                                                                        {moneyFmt(cf.subtotal - (parseFloat(cf.costo) || 0), cotizacion.moneda)}
                                                                    </span>
                                                                </div>}
                                                                <div className={`cf-col cf-total ${priceClass}`}>
                                                                    <span className="total-value">
                                                                        {blurMoney(cf.subtotal, cotizacion.moneda)}
                                                                    </span>
                                                                </div>
                                                                <div className="cf-col cf-actions">
                                                                    <button
                                                                        className="btn ghost small"
                                                                        type="button"
                                                                        onClick={() => eliminarCostoFijoServicio(servicio._id, cf._id)}
                                                                        title="Eliminar"
                                                                    >
                                                                        x
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Extras */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Extras (costo fijo)</h2>
                    <button
                        className="btn neon"
                        type="button"
                        onClick={agregarExtra}
                    >
                        + Agregar extra
                    </button>
                </div>

                {extras.length > 0 && (
                    <div className="extras-list">
                        {extras.map((e) => (
                            <div key={e._id} className="extra-item">
                                <div className="extra-content">
                                    <div className="extra-detalle">
                                        <TipTapEditor
                                            value={e.detalle}
                                            onChange={(html) => actualizarExtra(e._id, 'detalle', html)}
                                            placeholder="Descripcion del extra..."
                                        />
                                    </div>
                                    <div className="extra-costo">
                                        <span className="label">Costo ({cotizacion.moneda})</span>
                                        <input
                                            className="input"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={e.costo}
                                            onChange={(ev) => actualizarExtra(e._id, 'costo', ev.target.value)}
                                        />
                                    </div>
                                    <button
                                        className="btn-icon delete"
                                        type="button"
                                        onClick={() => eliminarExtra(e._id)}
                                        title="Eliminar extra"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {extras.length === 0 && (
                    <p className="empty-message">No hay extras agregados</p>
                )}
            </section>

            {/* Comentarios */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Comentarios</h2>
                </div>
                <div className="field">
                    <TipTapEditor
                        value={cotizacion.comentarios}
                        onChange={(html) => setCotizacion(c => ({ ...c, comentarios: html }))}
                        placeholder="Comentarios adicionales para la cotizacion..."
                    />
                </div>
            </section>

            {/* Resumen y acciones */}
            <section className="card">
                <div className="cardHead">
                    <h2 className="h2">Resumen</h2>
                </div>

                <div className="summary-grid">
                    <div className="summary">
                        {/* Valores internos - no van al PDF */}
                        {verDetalles && (
                            <>
                                <div className="sumRow interno-row">
                                    <span className="sumLabel">Costo Total</span>
                                    <span className="sumValue">{moneyFmt(computed.costoTotal, cotizacion.moneda)}</span>
                                </div>
                                <div className="sumRow interno-row">
                                    <span className="sumLabel">Ganancia Total</span>
                                    <span className={`sumValue ${computed.gananciaTotal >= 0 ? 'ganancia-positiva' : 'ganancia-negativa'}`}>
                                        {moneyFmt(computed.gananciaTotal, cotizacion.moneda)}
                                    </span>
                                </div>
                                <div className="divider interno-divider" />
                            </>
                        )}

                        {/* Valores que van al PDF */}
                        <div className={`sumRow ${priceClass}`}>
                            <span className="sumLabel">Subtotal</span>
                            <span className="sumValue">{blurMoney(computed.subtotal, cotizacion.moneda)}</span>
                        </div>
                        <div className={`sumRow ${priceClass}`}>
                            <span className="sumLabel">IGV ({Math.round(igvRate * 100)}%)</span>
                            <span className="sumValue">{blurMoney(computed.igv, cotizacion.moneda)}</span>
                        </div>
                        <div className={`sumRow ${priceClass}`}>
                            <span className="sumLabel">Subtotal Extras</span>
                            <span className="sumValue">{blurMoney(computed.subtotalExtras, cotizacion.moneda)}</span>
                        </div>
                        <div className="divider" />
                        <div className={`sumRow big ${priceClass}`}>
                            <span className="sumLabel">TOTAL</span>
                            <span className="sumValue">{blurMoney(computed.total, cotizacion.moneda)}</span>
                        </div>
                        <div className={`pill ${priceClass}`}>
                            Conversion: {blurMoney(computed.totalOther, computed.otherCurrency)}
                        </div>
                    </div>

                    <div className="actions-box">
                        <button
                            className="btn neon"
                            type="submit"
                            disabled={saving}
                        >
                            {saving ? 'Guardando...' : (cotizacionId ? 'Actualizar' : 'Guardar Cotización')}
                        </button>

                        {tienePermiso('ver_pdf') && (
                            <>
                                <button
                                    className="btn ghost"
                                    type="button"
                                    onClick={() => handleGenerarPDF(true)}
                                >
                                    Previsualizar PDF
                                </button>

                                <button
                                    className="btn ghost"
                                    type="button"
                                    onClick={() => handleGenerarPDF(false)}
                                >
                                    Descargar PDF
                                </button>
                            </>
                        )}

                        {onCancel && (
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={onCancel}
                            >
                                Cancelar
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Modal para ver datos del cliente */}
            {showClienteModal && clienteSeleccionado && (
                <div className="modal-overlay" onClick={() => setShowClienteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h3 className="modal-title">Datos del Cliente</h3>
                            <button
                                className="modal-close"
                                onClick={() => setShowClienteModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="cliente-detalle">
                                <div className="cliente-detalle-row">
                                    <span className="cliente-detalle-label">Nombre</span>
                                    <span className="cliente-detalle-value">{clienteSeleccionado.data.nombre || '-'}</span>
                                </div>
                                <div className="cliente-detalle-row">
                                    <span className="cliente-detalle-label">Empresa</span>
                                    <span className="cliente-detalle-value">{clienteSeleccionado.data.empresa || '-'}</span>
                                </div>
                                <div className="cliente-detalle-row">
                                    <span className="cliente-detalle-label">Email</span>
                                    <span className="cliente-detalle-value">{clienteSeleccionado.data.email || '-'}</span>
                                </div>
                                <div className="cliente-detalle-row">
                                    <span className="cliente-detalle-label">Teléfono</span>
                                    <span className="cliente-detalle-value">{clienteSeleccionado.data.telefono || '-'}</span>
                                </div>

                                {(clienteSeleccionado.data.contacto_emergencia_nombre ||
                                  clienteSeleccionado.data.contacto_emergencia_telefono ||
                                  clienteSeleccionado.data.contacto_emergencia_email) && (
                                    <>
                                        <hr className="cliente-detalle-divider" />
                                        <h4 className="cliente-detalle-subtitle">Contacto de Emergencia</h4>
                                        <div className="cliente-detalle-row">
                                            <span className="cliente-detalle-label">Nombre</span>
                                            <span className="cliente-detalle-value">{clienteSeleccionado.data.contacto_emergencia_nombre || '-'}</span>
                                        </div>
                                        <div className="cliente-detalle-row">
                                            <span className="cliente-detalle-label">Teléfono</span>
                                            <span className="cliente-detalle-value">{clienteSeleccionado.data.contacto_emergencia_telefono || '-'}</span>
                                        </div>
                                        <div className="cliente-detalle-row">
                                            <span className="cliente-detalle-label">Email</span>
                                            <span className="cliente-detalle-value">{clienteSeleccionado.data.contacto_emergencia_email || '-'}</span>
                                        </div>
                                    </>
                                )}

                                {clienteSeleccionado.data.notas && (
                                    <>
                                        <hr className="cliente-detalle-divider" />
                                        <div className="cliente-detalle-row">
                                            <span className="cliente-detalle-label">Notas</span>
                                            <span className="cliente-detalle-value">{clienteSeleccionado.data.notas}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn ghost"
                                onClick={() => setShowClienteModal(false)}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

export default CotizacionForm;
